import { config } from "../config";

/**
 * Cognito Hosted UI auth using Authorization Code + PKCE (public SPA client,
 * no secret). Flow:
 *   1. login() → build PKCE verifier/challenge, redirect to hosted UI.
 *   2. Cognito redirects to /auth/callback?code=... → exchangeCode().
 *   3. Tokens stored in localStorage; getAccessToken() refreshes when expired.
 */

const STORAGE = {
  verifier: "wt_pkce_verifier",
  tokens: "wt_tokens",
};

export interface Tokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
}

// --- PKCE helpers ---------------------------------------------------------

function base64url(bytes: Uint8Array): string {
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomVerifier(): string {
  const bytes = new Uint8Array(64);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

async function challengeFrom(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return base64url(new Uint8Array(digest));
}

// --- Token storage --------------------------------------------------------

export function getStoredTokens(): Tokens | undefined {
  const raw = localStorage.getItem(STORAGE.tokens);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as Tokens;
  } catch {
    return undefined;
  }
}

function storeTokens(t: Tokens): void {
  localStorage.setItem(STORAGE.tokens, JSON.stringify(t));
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE.tokens);
  localStorage.removeItem(STORAGE.verifier);
}

// --- Public flow ----------------------------------------------------------

/** Redirect the browser to the Cognito hosted UI login. */
export async function login(identityProvider?: "Google"): Promise<void> {
  const verifier = randomVerifier();
  sessionStorage.setItem(STORAGE.verifier, verifier);
  const challenge = await challengeFrom(verifier);

  const url = new URL(`${config.cognitoDomain}/oauth2/authorize`);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", config.cognitoClientId);
  url.searchParams.set("redirect_uri", config.redirectUri);
  url.searchParams.set("scope", "openid email profile");
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", challenge);
  if (identityProvider) url.searchParams.set("identity_provider", identityProvider);
  window.location.assign(url.toString());
}

function parseTokenResponse(json: {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
}): Tokens {
  return {
    accessToken: json.access_token,
    idToken: json.id_token,
    refreshToken: json.refresh_token ?? getStoredTokens()?.refreshToken ?? "",
    expiresAt: Date.now() + json.expires_in * 1000,
  };
}

/** Exchange an authorization code (at the callback) for tokens. */
export async function exchangeCode(code: string): Promise<Tokens> {
  const verifier = sessionStorage.getItem(STORAGE.verifier);
  if (!verifier) throw new Error("Missing PKCE verifier");

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.cognitoClientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier,
  });
  const resp = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!resp.ok) throw new Error(`Token exchange failed (${resp.status})`);
  const tokens = parseTokenResponse(await resp.json());
  storeTokens(tokens);
  sessionStorage.removeItem(STORAGE.verifier);
  return tokens;
}

async function refresh(refreshToken: string): Promise<Tokens | undefined> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: config.cognitoClientId,
    refresh_token: refreshToken,
  });
  const resp = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!resp.ok) return undefined;
  const tokens = parseTokenResponse(await resp.json());
  storeTokens(tokens);
  return tokens;
}

/** Get a valid access token, refreshing if within 60s of expiry. */
export async function getAccessToken(): Promise<string | undefined> {
  const tokens = getStoredTokens();
  if (!tokens) return undefined;
  if (Date.now() < tokens.expiresAt - 60_000) return tokens.accessToken;
  if (!tokens.refreshToken) return undefined;
  const refreshed = await refresh(tokens.refreshToken);
  return refreshed?.accessToken;
}

/** Decode basic claims from the id token (no signature verification — display only). */
export function getIdClaims():
  | { email?: string; sub?: string; "cognito:groups"?: string[] }
  | undefined {
  const tokens = getStoredTokens();
  if (!tokens?.idToken) return undefined;
  try {
    const payload = tokens.idToken.split(".")[1];
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return undefined;
  }
}

/** Logout via Cognito hosted UI (clears local tokens then redirects). */
export function logout(): void {
  clearTokens();
  const url = new URL(`${config.cognitoDomain}/logout`);
  url.searchParams.set("client_id", config.cognitoClientId);
  url.searchParams.set("logout_uri", `${config.appUrl}/`);
  window.location.assign(url.toString());
}
